import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { collection, doc, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection, useDocument } from '@/hooks/firestore';
import { Card, EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RatingStars } from '@/components/ui/RatingStars';
import { BookingSheet } from '@/components/booking/BookingSheet';
import { ServiceLongevity } from '@/components/services/ServiceLongevity';
import { SellerSafetyBadges } from '@/components/seller/SellerSafetyBadges';
import { useAuth } from '@/stores/auth';
import {
  CATEGORY_LABELS,
  PAYMENTS_ENABLED,
  formatZAR,
  type PublicProfileDoc,
  type ReviewDoc,
  type ServiceDoc,
} from '@makhe/shared';

// Editorial numbering for however many options the seller defined.
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const ref = serviceId ? doc(db, 'services', serviceId) : null;
  const { data: service, loading, error } = useDocument<ServiceDoc>(ref);
  const sellerRef = service ? doc(db, 'publicProfiles', service.sellerId) : null;
  const { data: sellerUser } = useDocument<PublicProfileDoc>(sellerRef);
  const { firebaseUser, isVerifiedStudent } = useAuth();
  const [bookOpen, setBookOpen] = useState(false);
  // Tiers are seller-defined — no fixed ids to assume. Default to the first.
  const [pickedTier, setPickedTier] = useState<string | null>(null);

  const reviewsQ = useMemo(() => {
    if (!serviceId) return null;
    return query(
      collection(db, 'reviews'),
      where('serviceId', '==', serviceId),
      where('isHidden', '==', false),
      orderBy('createdAt', 'desc'),
      limit(20),
    );
  }, [serviceId]);
  const { data: reviews } = useCollection<ReviewDoc>(reviewsQ);

  if (loading) return <LoadingSkeleton className="m-6 h-[80vh]" />;
  if (error)
    return (
      <div className="p-6">
        <EmptyState
          title="Couldn’t load this service"
          description="Something went wrong. Check your connection and try again."
        />
      </div>
    );
  if (!service)
    return (
      <div className="p-6">
        <EmptyState
          title="Service not found"
          description="This listing may have been removed or unpublished."
        />
      </div>
    );

  const wearWeeksValues = reviews
    .map((r) => r.wearWeeks)
    .filter((w): w is number => typeof w === 'number' && w > 0);
  const fromPriceZARcents = Math.min(...service.pricingTiers.map((t) => t.priceZARcents));
  const tier =
    (pickedTier && service.pricingTiers.some((t) => t.tier === pickedTier)
      ? pickedTier
      : service.pricingTiers[0]?.tier) ?? '';

  return (
    <section>
      {/* — Hero media — full bleed cinematic — */}
      <div className="relative bg-makhe-night">
        {service.media.videoURL ? (
          <video
            src={service.media.videoURL}
            poster={service.media.videoThumbnailURL}
            controls
            playsInline
            className="max-h-[78vh] w-full object-cover"
          />
        ) : service.media.photoURLs[0] ? (
          <img
            src={service.media.photoURLs[0]}
            alt={service.title}
            className="max-h-[78vh] w-full object-cover"
          />
        ) : (
          <div className="aspect-[3/4] w-full bg-makhe-line/20" />
        )}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/50 to-transparent" />
        <div className="absolute left-6 top-5 flex items-center gap-2 text-[11px] uppercase tracking-widest text-white">
          <span className="rounded-full bg-white/95 px-3 py-1 text-makhe-night">
            {CATEGORY_LABELS[service.category]}
          </span>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 pb-32 pt-10">
        {/* — Title + rating — */}
        <p className="eyebrow">A service by {service.sellerSnapshot.displayName}</p>
        <h1
          className="mt-5 font-display text-4xl leading-tight md:text-6xl"
          style={{ fontVariationSettings: '"opsz" 144, "wght" 400' }}
        >
          {service.title}
        </h1>

        <div className="mt-4 flex items-center gap-3 text-sm">
          <RatingStars value={service.stats.averageRating} size="sm" />
          <span className="text-makhe-muted">
            {service.stats.averageRating.toFixed(1)} · {service.stats.totalReviews} reviews
          </span>
        </div>

        {/* — Service longevity — how long the result actually lasts — */}
        <ServiceLongevity
          category={service.category}
          wearWeeksValues={wearWeeksValues}
          fromPriceZARcents={fromPriceZARcents}
        />

        {/* — Seller card — */}
        <Link
          to={`/seller/${service.sellerId}`}
          className="mt-8 flex items-center gap-4 rounded-md border border-makhe-line bg-makhe-paper p-4 transition-shadow hover:shadow-lift"
        >
          {service.sellerSnapshot.photoURL && (
            <img
              src={service.sellerSnapshot.photoURL}
              alt=""
              className="h-14 w-14 rounded-full object-cover ring-2 ring-makhe-bg"
            />
          )}
          <div className="flex-1">
            <p className="font-display text-xl">{service.sellerSnapshot.displayName}</p>
            <SellerSafetyBadges
              preferences={sellerUser?.sellerProfile?.safetyPreferences}
              className="mt-2"
            />
            <p className="mt-1 text-[11px] uppercase tracking-widest text-makhe-muted">
              View full profile →
            </p>
          </div>
          <span className="hidden text-[11px] uppercase tracking-widest text-makhe-ochre md:inline">
            ★ {service.sellerSnapshot.averageRating.toFixed(1)}
          </span>
        </Link>

        {/* — Description — drop cap — */}
        <div className="mt-12">
          <p className="eyebrow">The brief</p>
          <p
            className="mt-4 whitespace-pre-line text-[17px] leading-relaxed text-makhe-ink/85
                       first-letter:float-left first-letter:mr-3 first-letter:font-display
                       first-letter:text-6xl first-letter:leading-[0.9] first-letter:text-makhe-primary"
            style={{ fontVariationSettings: '"opsz" 14' }}
          >
            {service.description}
          </p>
        </div>

        {/* — Portfolio gallery — */}
        {service.media.photoURLs.length > 0 && (
          <div className="mt-16">
            <p className="eyebrow">The work</p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {service.media.photoURLs.map((url) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square overflow-hidden rounded-md bg-makhe-line/40"
                >
                  <img
                    src={url}
                    alt={`${service.title} — portfolio`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform hover:scale-105"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* — Pricing tiers — numbered editorial — */}
        <div className="mt-16">
          <div className="flex items-baseline justify-between border-b border-makhe-line pb-3">
            <p className="eyebrow">The tiers</p>
            <p className="font-display text-sm italic text-makhe-muted">
              {service.pricingTiers.length} option{service.pricingTiers.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="mt-6 grid gap-4">
            {service.pricingTiers.map((t, i) => {
              const selected = tier === t.tier;
              return (
                <button
                  key={t.tier}
                  onClick={() => setPickedTier(t.tier)}
                  className={`group flex items-center gap-6 rounded-md border p-5 text-left transition-all ${
                    selected
                      ? 'border-makhe-ink bg-makhe-ink text-makhe-bg shadow-lift'
                      : 'border-makhe-line bg-makhe-paper hover:border-makhe-ink'
                  }`}
                >
                  <span
                    className={`font-display text-5xl leading-none ${
                      selected ? 'text-makhe-ochre' : 'text-makhe-primary'
                    }`}
                    style={{ fontVariationSettings: '"opsz" 144, "wght" 300' }}
                  >
                    {ROMAN[i] ?? i + 1}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className="font-display text-xl">{t.label}</p>
                      <p className="font-mono text-lg">{formatZAR(t.priceZARcents)}</p>
                    </div>
                    <p
                      className={`mt-2 text-[11px] uppercase tracking-widest ${
                        selected ? 'text-makhe-bg/60' : 'text-makhe-muted'
                      }`}
                    >
                      {t.durationMinutes} min · {t.includes.length} included
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* — CTA — sellers can't book their own service — */}
        <div className="mt-12">
          {!firebaseUser ? (
            <Link
              to={`/auth/sign-in?next=${encodeURIComponent(`/service/${service.id}`)}`}
              className="block"
            >
              <Button className="w-full" size="lg" variant="ink">
                Sign in to book →
              </Button>
            </Link>
          ) : service.sellerId === firebaseUser?.uid ? (
            <Link to={`/seller/services/${service.id}/edit`} className="block">
              <Button className="w-full" size="lg" variant="ink">
                Edit your listing →
              </Button>
            </Link>
          ) : isVerifiedStudent() ? (
            <>
              <Button onClick={() => setBookOpen(true)} className="w-full" size="lg" variant="ink">
                Book this service →
              </Button>
              {PAYMENTS_ENABLED && (
                <p className="mt-3 text-center text-[11px] uppercase tracking-widest text-makhe-muted">
                  Money is held until you confirm the service was done
                </p>
              )}
            </>
          ) : (
            <Link to="/profile/verify-student" className="block">
              <Button className="w-full" size="lg" variant="ink">
                Verify your student status to book →
              </Button>
            </Link>
          )}
        </div>

        {/* — Reviews — */}
        <div id="reviews" className="mt-24">
          <div className="flex items-baseline justify-between border-b border-makhe-line pb-3">
            <p className="eyebrow">The verdict</p>
            <p className="font-display text-sm italic text-makhe-muted">
              {reviews.length} review{reviews.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="mt-6 space-y-4">
            {reviews.length === 0 && (
              <EmptyState title="No reviews yet" description="Be the first to share your experience." />
            )}
            {reviews.map((r) => (
              <Card key={r.id}>
                <div className="flex items-center justify-between">
                  <RatingStars value={r.rating} size="sm" />
                  <span className="text-[11px] uppercase tracking-widest text-makhe-muted">
                    {r.createdAt?.toDate?.().toLocaleDateString?.() ?? ''}
                  </span>
                </div>
                {/* Every review is tied to a completed, paid booking — so it's
                    genuinely verified. Show it; it's the strongest trust cue. */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-makhe-forest/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-makhe-forest">
                    ✓ Verified booking
                  </span>
                  {typeof r.wearWeeks === 'number' && r.wearWeeks > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-makhe-ochre/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest text-makhe-ochre">
                      Lasted {r.wearWeeks} {r.wearWeeks === 1 ? 'week' : 'weeks'}
                    </span>
                  )}
                </div>
                {r.textReview && (
                  <p className="mt-3 text-[15px] leading-relaxed text-makhe-ink/85">
                    {r.textReview}
                  </p>
                )}
                {r.videoReviewURL && (
                  <video
                    src={r.videoReviewURL}
                    controls
                    className="mt-4 max-h-72 w-full rounded-md"
                  />
                )}
                {r.photoURLs?.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {r.photoURLs.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-md bg-makhe-line/40">
                        <img src={url} alt="Review photo" loading="lazy" className="h-full w-full object-cover" />
                      </a>
                    ))}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </div>

      {firebaseUser && service.sellerId !== firebaseUser.uid && (
        <BookingSheet open={bookOpen} onClose={() => setBookOpen(false)} service={service} initialTier={tier} />
      )}
    </section>
  );
}
